import type React from 'react';
import { useEffect, useRef } from 'react';
import { Textarea } from '@zillow/constellation-10';

type TextareaAutosizeProps = {
  ariaLabel: string;
  isDisabled: boolean;
  onBlur?: () => void;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder: string;
  rows?: number;
  shouldFocus: boolean;
  value: string;
};

const TextareaAutoSize = ({
  ariaLabel,
  isDisabled,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  rows,
  shouldFocus,
  value,
}: TextareaAutosizeProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInput = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto'; // Reset height to auto
      textarea.style.height = `${textarea.scrollHeight}px`; // Set new height based on scroll height
    }
  };

  // A wrapping placeholder affects scrollHeight, so re-fit on placeholder change as well as on mount
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [placeholder]);

  useEffect(() => {
    if (shouldFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [shouldFocus]);

  return (
    <Textarea
      ref={textareaRef}
      data-testid="textarea-autosize"
      aria-label={ariaLabel}
      disabled={isDisabled}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onInput={handleInput}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      rows={rows}
      value={value}
      css={{
        backgroundColor: 'bg.neutral',
        minHeight: '44px',
        maxHeight: '115px',
        lg: {
          boxShadowColor: 'bg.neutral',
        },
      }}
    />
  );
};

export default TextareaAutoSize;
